import styled from '@emotion/styled'
import { MotionStyle } from 'framer-motion'
import { Component } from 'react'
import { ConfigurationDetail, IdentifierType } from 'stylewhere/api'
import { Box, Icons, Text, Button, Input, ItemIdentifierInfoModal } from 'stylewhere/components'
import { T, __ } from 'stylewhere/shared/i18n'
import { Spinner } from '@chakra-ui/react'
import { closeModal, openModal } from 'stylewhere/shared/utils'

interface Props {
  marginBottom?: number
  widthPerc?: number
  inLine?: boolean
  identifier: ConfigurationDetail
  style?: MotionStyle
  buttons?: { text: string; onClick: () => void }[]
  onInputSubmit?: (value: string) => void
  layout?: any
  isAssociation?: boolean
  identifiersCount?: number
  labelsByType?: Partial<Record<IdentifierType, string>>
  hideCode?: boolean
  hideStatusLabel?: boolean
  renderSimpleItemIdentifierAsNormalIdentifier?: boolean
  showIdentifierDetail?: boolean
}

const LoadingContainer = styled(Box)`
  width: 80px;
  height: 80px;
  margin-bottom: 0px;
  margin-right: 30px;
  justify-content: center;
  align-items: center;
`

const UhfTagIcon = (props: any) => <Icons.Rfid {...props} />
const NfcTagIcon = (props: any) => <Icons.Nfc {...props} />
const SimpleItemIcon = (props: any) => <Icons.Serial {...props} />
const AssociationIcon = (props: any) =>
  props.status === 'ERROR' ? (
    <Icons.AssociationError {...props} />
  ) : props.status === 'WRITING' ? (
    <LoadingContainer>
      <Spinner size="xl" color="#000000" />
    </LoadingContainer>
  ) : (
    <Icons.Encoding {...props} />
  )

const identifierTypes = {
  UHF_TAG: { icon: UhfTagIcon, text: 'TAG UHF' },
  NFC_TAG: { icon: NfcTagIcon, text: 'TAG NFC' },
  SIMPLE_ITEM_IDENTIFIER: { icon: SimpleItemIcon, text: 'IDENTIFIER' },
  ASSOCIATION: { icon: AssociationIcon, text: __(T.misc.association) }
}

export class IdentifierBox extends Component<Props> {
  textColor = {
    ERROR: 'white',
    DEFAULT: 'black'
  }

  status = {
    CONFIRMED: __(T.misc.confirmed),
    ERROR: __(T.error.error),
    TO_BE_READ: __(T.misc.reading) + '...',
    PROCESSING: __(T.misc.processing) + '...',
    IGNORED: __(T.misc.ignored),
    SKIPPED: __(T.misc.skipped),
    WRITING: __(T.misc.writing_progress) + '...',
    READY: __(T.misc.ready_to_read),
    EXPECTED: __(T.misc.expected)
  }

  private resolveWidthPerc(isSimpleItemIdentifier: boolean): number {
    const { layout, inLine, widthPerc, isAssociation, identifiersCount } = this.props
    let result = layout?.widthPerc ?? (isSimpleItemIdentifier && !inLine ? 100 : widthPerc || 100)
    if (isAssociation && layout && identifiersCount) {
      result = inLine ? 100 : identifiersCount === 1 || identifiersCount % 2 === 0 ? 100 : 49
    }
    return result
  }

  private resolveTextColor(identifier: ConfigurationDetail): string {
    const color = this.textColor[identifier._colorStatus ?? (identifier._status as any)] ?? this.textColor.DEFAULT
    if (identifier.identifierType === 'ASSOCIATION' && identifier._status === 'ERROR') return this.textColor.DEFAULT
    return color
  }

  private resolveIconStyle(layout: any, isFullWidth: boolean) {
    const iconSize = layout?.iconSize ?? (isFullWidth ? 80 : 60)
    return {
      width: iconSize,
      height: iconSize,
      marginBottom: layout?.iconMarginBottom ?? (isFullWidth ? 0 : 10),
      marginRight: layout?.iconMarginRight ?? (isFullWidth ? 30 : 0)
    }
  }

  private readonly openDetail = () => {
    const { identifier } = this.props
    return openModal({
      id: 'item-identifier-modal',
      closeOnOverlayClick: true,
      modal: (
        <ItemIdentifierInfoModal visible identifier={identifier} onClose={() => closeModal('item-identifier-modal')} />
      )
    })
  }

  private renderContent(
    isSimpleItemIdentifier: boolean,
    textColor: string,
    label: string | undefined,
    idfTypeText: string | undefined,
    isFullWidth: boolean
  ) {
    const {
      identifier,
      layout,
      hideCode,
      hideStatusLabel,
      renderSimpleItemIdentifierAsNormalIdentifier,
      onInputSubmit,
      showIdentifierDetail
    } = this.props
    return (
      <Box row center>
        <Box flex>
          {(!isSimpleItemIdentifier || renderSimpleItemIdentifierAsNormalIdentifier) && (
            <>
              <Text
                bold
                data-testid={`identifier-role-${identifier.role}`}
                fontSize={layout?.fontSizeTagType ?? 24}
                style={{ marginBottom: isFullWidth ? 5 : 0, color: textColor }}
              >
                {label}
              </Text>
              {identifier.code && !hideCode && (
                <Text
                  data-testid={`identifier-code-${identifier.code}`}
                  fontSize={layout?.fontSizeCode ?? 16}
                  bold
                  style={{ color: textColor }}
                >
                  {identifier.code}
                </Text>
              )}
              {!hideStatusLabel && (
                <Text bold fontSize={layout?.fontSizeStatus ?? 20} style={{ color: textColor }}>
                  {identifier._customStatusText ?? this.status[identifier._status ?? 'TO_BE_READ']}
                </Text>
              )}
            </>
          )}
          {!!isSimpleItemIdentifier && !renderSimpleItemIdentifierAsNormalIdentifier && onInputSubmit && (
            <Input
              testId={`identifier-role-${identifier.role}`}
              disabled={!['TO_BE_READ', 'EXPECTED'].includes(identifier._status ?? 'TO_BE_READ')}
              startFocus
              defaultValue={identifier.code}
              onEnter={onInputSubmit}
              label={identifier.role || idfTypeText}
            />
          )}
          {identifier._error && (
            <Text fontSize={18} style={{ marginTop: 5, color: textColor }}>
              {identifier._error}
            </Text>
          )}
          {identifier._errorMessage && (
            <Text fontSize={18} style={{ marginTop: 5, color: textColor }}>
              {identifier._errorMessage}
            </Text>
          )}
        </Box>
        {identifier.identifierId && showIdentifierDetail && (
          <Box>
            <Icons.Info
              style={{
                color:
                  identifier._error && (identifier._colorStatus ?? identifier._status) === 'ERROR' ? '#FFF' : '#000'
              }}
            />
          </Box>
        )}
      </Box>
    )
  }

  private renderButtons() {
    const { buttons } = this.props
    if (!buttons) return null
    return (
      <Box center>
        {buttons.map((button, index) => (
          <IdentifierButton key={'identifier-button-' + index} title={button.text} onClick={button.onClick} />
        ))}
      </Box>
    )
  }

  render() {
    const { marginBottom = 16, identifier, style, layout, inLine, labelsByType, showIdentifierDetail } = this.props

    const isSimpleItemIdentifier =
      identifier.identifierType === 'SIMPLE_ITEM_IDENTIFIER' &&
      identifier._status !== 'CONFIRMED' &&
      identifier._status !== 'ERROR'

    const widthPerc = this.resolveWidthPerc(isSimpleItemIdentifier)
    const isFullWidth = inLine || widthPerc === 100
    const iconStyle = this.resolveIconStyle(layout, isFullWidth)
    const textColor = this.resolveTextColor(identifier)
    const idfType = identifierTypes[identifier.identifierType]
    const label = labelsByType?.[identifier.identifierType] ?? (identifier.role || idfType?.text)

    return (
      <IdentifierContainer
        data-testid={`identifier-box`}
        data-cy={identifier.code}
        status={identifier._colorStatus ?? identifier._status}
        identifierType={identifier.identifierType}
        style={style}
        pv={layout?.paddingVertical ?? 10}
        ph={layout?.paddingHorizontal ?? 30}
        row
        widthPerc={widthPerc}
        mb={marginBottom}
        minHeight={layout?.minHeight ?? '175px'}
        maxHeight={layout?.maxHeight ?? '40vh'}
        onClick={identifier.identifierId && showIdentifierDetail ? this.openDetail : undefined}
      >
        <Box vcenter flex row={layout?.row || isFullWidth}>
          {idfType?.icon({ style: iconStyle, fill: textColor, status: identifier._status })}
          <Box flex={1}>{this.renderContent(isSimpleItemIdentifier, textColor, label, idfType?.text, isFullWidth)}</Box>
        </Box>
        {this.renderButtons()}
      </IdentifierContainer>
    )
  }
}

const IdentifierContainer = styled(Box)<{
  widthPerc: number
  status?:
    | 'CONFIRMED'
    | 'ERROR'
    | 'TO_BE_READ'
    | 'PROCESSING'
    | 'IGNORED'
    | 'SKIPPED'
    | 'WRITING'
    | 'NFC_COLLISION'
    | 'READY'
    | 'EXPECTED'
  identifierType?: IdentifierType
  minHeight?: string
  maxHeight?: boolean
}>`
  background-color: white;
  border-radius: 15px;
  min-height: ${({ minHeight }) => minHeight || '225px'};
  max-height: ${({ maxHeight }) => maxHeight || '40vh'};
  align-items: center;
  box-shadow:
    0px 1px 4px rgb(0 0 0 / 15%),
    inset 0px 0px 7px rgba(0, 0, 0, 0.1);
  width: ${({ widthPerc = 100 }) => (widthPerc === 100 ? `100%;` : `calc(${widthPerc}% - 4px);`)};
  ${({ status }) => status === 'ERROR' && `background-color: #EB5757;`}
  ${({ status }) => status === 'NFC_COLLISION' && `background-color: #EB5757;`}
  ${({ status }) => status === 'CONFIRMED' && `background-color: #75EBA8;`}
  ${({ status }) => status === 'IGNORED' && `background-color: #F2F2F2;`}
  ${({ status }) => status === 'SKIPPED' && `background-color: #F2F2F2;`}
  ${({ status }) => status === 'PROCESSING' && `box-shadow:inset 0 0px 10px rgb(117 235 168 / 70%);`}
  ${({ status }) => status === 'WRITING' && `box-shadow:inset 0 0px 10px rgb(245 167 66 / 70%);`}
  ${({ status, identifierType }) =>
    status === 'ERROR' && identifierType === 'ASSOCIATION' && `background-color: #E0E0E0;`}
     ${({ status = 'TO_BE_READ', identifierType }) =>
    status === 'TO_BE_READ' && identifierType === 'SIMPLE_ITEM_IDENTIFIER' && `background-color: #E0E0E0;`}
`

const IdentifierButton = styled((props) => <Button {...props} />)`
  background: #333333;
  color: white;
  border-radius: 100px;
  flex: 0;
  width: fit-content;
  min-width: 200px;
  padding: 0px 50px;
`
